import { ChatMessages } from "@storytell/cp-client";

const END_TURN_OPERATION_ID = "operation_00000000000000000000"; // Special operation ID for end of conversation

export const isEndTurnOperationId = (operationId: string): boolean => operationId === END_TURN_OPERATION_ID;

// UI Renderer types
export enum BlockRenderer {
  USER_FACING_MESSAGE = "UserFacingMessage",
  BLOCK_PREVIEW = "BlockPreview",
  ARTIFACT_VIEWER = "ArtifactViewer",
  TASK_CARD = "TaskCard",
  PROMPT_ACK = "PromptAck",
  INTERSTITIAL_DATA = "InterstitialData",
  INTERSTITIAL_USER_FACING = "InterstitialUserFacing",
  INTERSTITIAL_CATCH = "InterstitialCatch",
  INTERSTITIAL = "Interstitial",
  TURN_COMPLETE = "TurnComplete",
  CHAT_SEARCH_RESULTS = "ChatSearchResults",
  SKILLS = "Skills",
  UNKNOWN = "Unknown", // Fallback for unknown types
}

const renderMapping: Record<ChatMessages.MessageType, BlockRenderer> = {
  [ChatMessages.MessageType.UNSPECIFIED]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.PROMPT_ACK]: BlockRenderer.PROMPT_ACK,
  [ChatMessages.MessageType.INTERSTITIAL]: BlockRenderer.INTERSTITIAL,
  [ChatMessages.MessageType.INTERSTITIAL_DATA]: BlockRenderer.INTERSTITIAL_DATA,
  [ChatMessages.MessageType.INTERSTITIAL_USER_FACING]: BlockRenderer.INTERSTITIAL_USER_FACING,
  [ChatMessages.MessageType.INTERSTITIAL_CATCH]: BlockRenderer.INTERSTITIAL_CATCH,
  [ChatMessages.MessageType.SCOPED]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.REFERENCED]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.RATE_FEEDBACK]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.SYSTEM_STATUS]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.MEMORY_UPDATE]: BlockRenderer.UNKNOWN,
  [ChatMessages.MessageType.TASK_CREATE]: BlockRenderer.TASK_CARD,
  [ChatMessages.MessageType.TASK_UPDATE]: BlockRenderer.TASK_CARD,
  [ChatMessages.MessageType.OPERATION_STATUS]: BlockRenderer.TASK_CARD,
  [ChatMessages.MessageType.CONTENT]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_EXPLANATION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_CITATION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_SUMMARY]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_DISCLAIMER]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_ALTERNATIVE]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_ELABORATION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_PREREQUISITE]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_CLARIFICATION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_NEXT_STEPS]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_ERROR_CORRECTION]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_WALKTHROUGH]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_TECHNICAL_DETAILS]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_HISTORICAL_CONTEXT]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_COMPARISON]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.CONTENT_EXAMPLE]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.DATA_VISUALIZATION]: BlockRenderer.BLOCK_PREVIEW,
  [ChatMessages.MessageType.SUGGESTED_ACTION]: BlockRenderer.BLOCK_PREVIEW,
  [ChatMessages.MessageType.CLARIFICATION_REQUEST]: BlockRenderer.USER_FACING_MESSAGE,
  [ChatMessages.MessageType.ARTIFACT_CREATION]: BlockRenderer.ARTIFACT_VIEWER,
  [ChatMessages.MessageType.ARTIFACT_REVISION]: BlockRenderer.ARTIFACT_VIEWER,
  [ChatMessages.MessageType.CHAT_SEARCH_RESULTS]: BlockRenderer.CHAT_SEARCH_RESULTS,
  [ChatMessages.MessageType.SKILLS]: BlockRenderer.SKILLS,
};

export function determinePreferredRenderer(block: { messageType?: ChatMessages.MessageType }): BlockRenderer {
  if (block.messageType && renderMapping[block.messageType]) {
    return renderMapping[block.messageType];
  }
  return BlockRenderer.UNKNOWN;
}

interface FeatureTokens {
  ingressCredits?: number;
  egressCredits?: number;
  totalCredits?: number;
}

// Single aggregated metrics across all features (for simple real-time display)
export interface AggregatedMetrics {
  ingressTokens: number;
  egressTokens: number;
  ingressBytes: number;
  egressBytes: number;
  // Credit information from featureTokens
  totalCredits?: number;
  ingressCredits?: number;
  egressCredits?: number;
}

// Utility function to accumulate metrics (simple aggregation)
export function accumulateAggregatedMetrics(
  currentMetrics: AggregatedMetrics,
  ingressTokens: number,
  egressTokens: number,
  ingressBytes: number,
  egressBytes: number,
  featureTokens?: FeatureTokens
): AggregatedMetrics {
  return {
    ingressTokens: currentMetrics.ingressTokens + ingressTokens,
    egressTokens: currentMetrics.egressTokens + egressTokens,
    ingressBytes: currentMetrics.ingressBytes + ingressBytes,
    egressBytes: currentMetrics.egressBytes + egressBytes,
    totalCredits: featureTokens?.totalCredits
      ? (currentMetrics.totalCredits || 0) + featureTokens.totalCredits
      : currentMetrics.totalCredits,
    ingressCredits: featureTokens?.ingressCredits
      ? (currentMetrics.ingressCredits || 0) + featureTokens.ingressCredits
      : currentMetrics.ingressCredits,
    egressCredits: featureTokens?.egressCredits
      ? (currentMetrics.egressCredits || 0) + featureTokens.egressCredits
      : currentMetrics.egressCredits,
  };
}
